/**
 * InteractiveObject - Base class for interactive objects in the level
 * Includes switches, buttons, levers, computers, etc.
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import type { Player } from './Player';

export type InteractiveObjectType =
  | 'switch'
  | 'button'
  | 'lever'
  | 'computer'
  | 'panel'
  | 'valve'
  | 'generator';

export interface InteractiveObjectConfig {
  id: string;
  type: InteractiveObjectType;
  position: Vector2;
  size: { width: number; height: number };
  requiresPower?: boolean;
  oneTimeUse?: boolean;
  cooldownTime?: number; // seconds
  metadata?: Record<string, any>;
}

export class InteractiveObject extends Entity {
  // Object properties
  public objectId: string;
  public objectType: InteractiveObjectType;
  public requiresPower: boolean;
  public oneTimeUse: boolean;
  public cooldownTime: number;
  public metadata: Record<string, any>;

  // State
  private isActivated: boolean = false;
  private isPowered: boolean = true;
  private isUsed: boolean = false;
  private cooldownRemaining: number = 0;
  private interactionRadius: number = 50; // pixels
  private isPlayerNearby: boolean = false;
  private mesh: THREE.Group | null = null;

  // Callbacks
  private onActivateCallback: (() => void) | null = null;
  private onDeactivateCallback: (() => void) | null = null;

  constructor(config: InteractiveObjectConfig) {
    super(config.id, 'interactive_object', config.position, config.size);

    this.objectId = config.id;
    this.objectType = config.type;
    this.requiresPower = config.requiresPower ?? false;
    this.oneTimeUse = config.oneTimeUse ?? false;
    this.cooldownTime = config.cooldownTime ?? 0;
    this.metadata = config.metadata || {};

    // Interactive objects don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create visual mesh
    this.createMesh();
  }

  /**
   * Create Three.js mesh for the interactive object
   */
  private createMesh(): void {
    this.mesh = new THREE.Group();

    // Create main body with different shapes based on type
    let bodyGeometry: THREE.BufferGeometry;
    switch (this.objectType) {
      case 'switch':
      case 'button':
        bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 4);
        break;
      case 'lever':
        bodyGeometry = new THREE.CylinderGeometry(this.size.width / 4, this.size.width / 4, this.size.height, 8);
        break;
      case 'computer':
      case 'panel':
        bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 2);
        break;
      case 'valve':
        bodyGeometry = new THREE.CylinderGeometry(this.size.width / 2, this.size.width / 2, 4, 6);
        break;
      case 'generator':
        bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 8);
        break;
      default:
        bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 2);
    }

    const bodyMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.mesh.add(bodyMesh);

    // Add border/outline for better visibility
    const borderGeometry = new THREE.BufferGeometry();
    const borderPoints: THREE.Vector3[] = [
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 2),
      new THREE.Vector3(this.size.width / 2, -this.size.height / 2, 2),
      new THREE.Vector3(this.size.width / 2, this.size.height / 2, 2),
      new THREE.Vector3(-this.size.width / 2, this.size.height / 2, 2),
      new THREE.Vector3(-this.size.width / 2, -this.size.height / 2, 2),
    ];
    borderGeometry.setFromPoints(borderPoints);
    const borderMaterial = new THREE.LineBasicMaterial({ color: 0x808080, linewidth: 2 });
    const borderLine = new THREE.Line(borderGeometry, borderMaterial);
    this.mesh.add(borderLine);

    // Add indicator light for activation status
    const indicatorGeometry = new THREE.CircleGeometry(this.size.width / 8, 8);
    const indicatorMaterial = new THREE.MeshBasicMaterial({ color: 0x404040 });
    const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
    indicator.position.set(0, this.size.height / 3, 2.5);
    this.mesh.add(indicator);

    this.mesh.position.set(this.position.x, this.position.y, 0);
  }

  /**
   * Update interactive object state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update cooldown
    if (this.cooldownRemaining > 0) {
      this.cooldownRemaining = Math.max(0, this.cooldownRemaining - deltaTime);
    }

    // Update mesh color based on player proximity and state
    if (this.mesh && this.mesh.children.length >= 3) {
      // Update body color
      const bodyMesh = this.mesh.children[0] as THREE.Mesh;
      const bodyMaterial = bodyMesh.material as THREE.MeshBasicMaterial;

      if (this.cooldownRemaining > 0) {
        bodyMaterial.color.setHex(0x606060); // Dark gray when on cooldown
      } else if (this.isActivated) {
        bodyMaterial.color.setHex(0x00ff00); // Green when activated
      } else if (this.isPlayerNearby) {
        bodyMaterial.color.setHex(0xffff80); // Light yellow when nearby
      } else {
        bodyMaterial.color.setHex(0xcccc00); // Darker yellow when idle
      }

      // Update border color for better feedback
      const borderLine = this.mesh.children[1] as THREE.Line;
      const borderMaterial = borderLine.material as THREE.LineBasicMaterial;
      if (this.isPlayerNearby) {
        borderMaterial.color.setHex(0x00ff00); // Green border when nearby
      } else {
        borderMaterial.color.setHex(0x808080); // Gray border normally
      }

      // Update indicator light
      const indicator = this.mesh.children[2] as THREE.Mesh;
      const indicatorMaterial = indicator.material as THREE.MeshBasicMaterial;
      if (this.cooldownRemaining > 0) {
        indicatorMaterial.color.setHex(0xff4040); // Red when on cooldown
      } else if (this.isActivated) {
        indicatorMaterial.color.setHex(0x00ff00); // Green when activated
      } else if (!this.isPowered) {
        indicatorMaterial.color.setHex(0x404040); // Dark gray when no power
      } else {
        indicatorMaterial.color.setHex(0x808080); // Gray when ready
      }
    }
  }

  /**
   * Render object (placeholder for Three.js rendering)
   */
  public render(): void {
    // Will be implemented with Three.js sprite rendering
  }

  /**
   * Check if player can interact with this object
   */
  public canInteract(player: Player): boolean {
    if (this.destroyed || !this.active) {
      return false;
    }

    if (this.oneTimeUse && this.isUsed) {
      return false;
    }

    if (this.requiresPower && !this.isPowered) {
      return false;
    }

    if (this.cooldownRemaining > 0) {
      return false;
    }

    const distance = this.distanceTo(player);
    return distance <= this.interactionRadius;
  }

  /**
   * Interact with the object
   */
  public interact(player: Player): boolean {
    if (!this.canInteract(player)) {
      return false;
    }

    // Toggle activation
    if (this.isActivated) {
      this.deactivate();
    } else {
      this.activate();
    }

    // Mark as used if one-time use
    if (this.oneTimeUse) {
      this.isUsed = true;
    }

    // Start cooldown
    if (this.cooldownTime > 0) {
      this.cooldownRemaining = this.cooldownTime;
    }

    console.log(`Player interacted with '${this.objectId}' (${this.objectType})`);
    return true;
  }

  /**
   * Activate the object
   */
  public activate(): void {
    if (this.isActivated) {
      return;
    }

    this.isActivated = true;
    console.log(`Object '${this.objectId}' activated`);

    // Call activation callback
    if (this.onActivateCallback) {
      this.onActivateCallback();
    }
  }

  /**
   * Deactivate the object
   */
  public deactivate(): void {
    if (!this.isActivated) {
      return;
    }

    this.isActivated = false;
    console.log(`Object '${this.objectId}' deactivated`);

    // Call deactivation callback
    if (this.onDeactivateCallback) {
      this.onDeactivateCallback();
    }
  }

  /**
   * Set power state
   */
  public setPower(powered: boolean): void {
    this.isPowered = powered;

    if (!powered && this.isActivated) {
      this.deactivate();
    }

    console.log(`Object '${this.objectId}' power: ${powered ? 'ON' : 'OFF'}`);
  }

  /**
   * Reset the object
   */
  public reset(): void {
    this.isActivated = false;
    this.isUsed = false;
    this.cooldownRemaining = 0;
    console.log(`Object '${this.objectId}' reset`);
  }

  /**
   * Set activation callback
   */
  public setOnActivate(callback: () => void): void {
    this.onActivateCallback = callback;
  }

  /**
   * Set deactivation callback
   */
  public setOnDeactivate(callback: () => void): void {
    this.onDeactivateCallback = callback;
  }

  // Getters
  public getIsActivated(): boolean {
    return this.isActivated;
  }

  public getIsPowered(): boolean {
    return this.isPowered;
  }

  public getIsUsed(): boolean {
    return this.isUsed;
  }

  public getCooldownRemaining(): number {
    return this.cooldownRemaining;
  }

  public getObjectType(): InteractiveObjectType {
    return this.objectType;
  }

  public getMetadata(): Record<string, any> {
    return { ...this.metadata };
  }

  public setPlayerNearby(nearby: boolean): void {
    this.isPlayerNearby = nearby;
  }

  public getMesh(): THREE.Group | null {
    return this.mesh;
  }

  public setMesh(mesh: THREE.Group): void {
    this.mesh = mesh;
  }

  /**
   * Get object info for UI/debugging
   */
  public getInfo(): {
    id: string;
    type: InteractiveObjectType;
    activated: boolean;
    powered: boolean;
    used: boolean;
    cooldown: number;
    canUse: boolean;
  } {
    return {
      id: this.objectId,
      type: this.objectType,
      activated: this.isActivated,
      powered: this.isPowered,
      used: this.isUsed,
      cooldown: this.cooldownRemaining,
      canUse: !this.isUsed && this.cooldownRemaining === 0 && (!this.requiresPower || this.isPowered),
    };
  }

  /**
   * Clean up resources
   */
  public override destroy(): void {
    if (this.mesh) {
      this.mesh.children.forEach(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        } else if (child instanceof THREE.Line) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
      this.mesh = null;
    }

    super.destroy();
  }
}
